/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, AlertCircle, RefreshCcw, XCircle } from "lucide-react";
import api from "../../utils/api";
import Swal from "sweetalert2";

const RegisterComplete = () => {
    const [status, setStatus] = useState("checking");
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const getTokenFromUrl = () => {
        const params = new URLSearchParams(location.search);
        return params.get("token");
    };

    const verifyPayment = async (registrationToken) => {
        try {
            const res = await api.post("/office-registration/verify-payment", {
                registration_token: registrationToken,
            });
            // If the API returns payment_completed: true, verification is successful
            return res.data.data.payment_completed === true;
        } catch (err) {
            console.error("Verify payment error:", err);
            return false;
        }
    };

    useEffect(() => {
        const token = getTokenFromUrl();

        if (!token) {
            setStatus("error");
            Swal.fire({
                icon: "error",
                title: t("register_complete.invalid_link_title", "Invalid Link"),
                text: t("register_complete.invalid_link_text", "Registration token is missing from the URL."),
                confirmButtonText: t("register_complete.back_to_register", "Back to Registration"),
            }).then(() => navigate(`/${i18n.language}/register?type=office`));
            return;
        }

        // Store token for subsequent registration steps if needed
        localStorage.setItem("registration_token", token);

        const check = async () => {
            const paid = await verifyPayment(token);

            if (paid) {
                setStatus("success");
                Swal.fire({
                    icon: "success",
                    title: t("register_complete.success_title", "Payment Successful!"),
                    text: t("register_complete.success_text", "Your payment has been confirmed. Redirecting to complete registration..."),
                    timer: 3000,
                    showConfirmButton: false,
                }).then(() => {
                    navigate(`/${i18n.language}/register?type=office`, { replace: true });
                });
            } else {
                setStatus("pending");
                Swal.fire({
                    icon: "warning",
                    title: t("register_complete.processing_title", "Payment Still Processing"),
                    text: t("register_complete.processing_text", "We haven't received confirmation yet. Please wait a moment and try again."),
                    confirmButtonText: t("register_complete.check_again", "Check Again"),
                    cancelButtonText: t("register_complete.cancel", "Cancel"),
                    showCancelButton: true,
                }).then((result) => {
                    if (result.isConfirmed) window.location.reload();
                    else navigate(`/${i18n.language}/register?type=office`);
                });
            }
        };

        check();
    }, [location, navigate, i18n.language, t]);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white p-8 rounded-3xl shadow-xl text-center"
                >
                    {status === "checking" && (
                        <>
                            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-6">
                                <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {t("register_complete.verifying", "Verifying Payment...")}
                            </h2>
                            <p className="text-gray-500">
                                {t("register_complete.please_wait", "Please wait while we confirm your transaction.")}
                            </p>
                        </>
                    )}

                    {status === "success" && (
                        <>
                            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
                                <CheckCircle className="h-10 w-10 text-green-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {t("register_complete.success_title", "Payment Confirmed!")}
                            </h2>
                            <p className="text-gray-500">
                                {t("register_complete.redirecting", "Redirecting you to finish registration...")}
                            </p>
                        </>
                    )}

                    {status === "pending" && (
                        <>
                            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-yellow-100 mb-6">
                                <RefreshCcw className="h-10 w-10 text-yellow-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {t("register_complete.processing_title", "Payment Processing")}
                            </h2>
                            <p className="text-gray-500 mb-6">
                                {t("register_complete.processing_text", "Please wait or check again in a few seconds.")}
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="w-full flex items-center justify-center gap-2 px-8 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-lg"
                            >
                                <RefreshCcw className="w-5 h-5" />
                                {t("register_complete.check_again", "Check Again")}
                            </button>
                        </>
                    )}

                    {status === "error" && (
                        <>
                            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
                                <XCircle className="h-10 w-10 text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                {t("register_complete.failed_title", "Verification Failed")}
                            </h2>
                            <p className="text-gray-500 mb-6">
                                {t("register_complete.failed_text", "We couldn't verify your payment. Please contact support.")}
                            </p>
                            <button
                                onClick={() => navigate(`/${i18n.language}/contact`)}
                                className="w-full flex items-center justify-center gap-2 px-8 py-3 border border-gray-300 text-base font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                            >
                                {t("header.nav.contact", "Contact Us")}
                            </button>
                        </>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default RegisterComplete;